import { Workspace } from "@/types/generated/management-api-types";

/**
 * A row of the left column: an organization and the workspaces it holds. The
 * demo workspaces sit under one made-up organization of their own.
 */
export type OrganizationGroup = {
    key: string;
    /** Empty for an organization nobody named yet. */
    name: string;
    /** The line under the name. */
    detail: string;
    favicon?: string;
    /** A real organization, which a new workspace can be added to. */
    isOrganization: boolean;
    workspaces: Workspace[];
};

export const DEMO_WORKSPACES_KEY = "__demo_workspaces__";

export function groupByOrganization(
    workspaces: Workspace[],
): OrganizationGroup[] {
    const organizations = new Map<string, OrganizationGroup>();
    const demoWorkspaces: Workspace[] = [];

    workspaces.forEach((workspace) => {
        if (workspace.isDemo) {
            demoWorkspaces.push(workspace);
            return;
        }
        const { id, name, favicon } = workspace.organization;
        const group = organizations.get(id) ?? {
            key: id,
            name: name ?? "",
            detail: id,
            favicon,
            isOrganization: true,
            workspaces: [],
        };
        group.workspaces.push(workspace);
        organizations.set(id, group);
    });

    if (demoWorkspaces.length === 0) {
        return [...organizations.values()];
    }

    return [
        {
            key: DEMO_WORKSPACES_KEY,
            name: "Demo workspaces",
            detail:
                demoWorkspaces.length === 1
                    ? "1 workspace"
                    : `${demoWorkspaces.length} workspaces`,
            isOrganization: false,
            workspaces: demoWorkspaces,
        },
        ...organizations.values(),
    ];
}

const matches = (needle: string, ...values: string[]) =>
    values.some((value) => value.toLowerCase().includes(needle));

/**
 * What a search leaves of the picker. An organization that matches keeps all
 * of its workspaces: searching for a client is how its workspaces are found,
 * and none of them need be named after it. Otherwise only the workspaces that
 * match stay, and an organization left without any is dropped.
 */
export function filterGroups(
    groups: OrganizationGroup[],
    query: string,
): OrganizationGroup[] {
    const needle = query.trim().toLowerCase();
    if (needle === "") return groups;

    return groups
        .map((group) =>
            matches(needle, group.name, group.detail)
                ? group
                : {
                      ...group,
                      workspaces: group.workspaces.filter((workspace) =>
                          matches(needle, workspace.name, workspace.id),
                      ),
                  },
        )
        .filter((group) => group.workspaces.length > 0);
}
